// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Body,
  Button,
  Description,
  InlineAlert,
  InputCheckbox,
  LgPadding,
  Link,
  MdSpacing,
  Toast,
  ToastContext,
  XsSpacing,
} from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
// eslint-disable-next-line import/no-deprecated
import { applyManualCoupon } from '~shared/actions/manualCoupons';
import { getNextDeliveryForPrescriptionID } from '~shared/features/delivery/selectors/getNextDeliveryForPrescriptionID';
import { getAvailableManualCoupon } from '~shared/features/pricing/selectors/getAvailableManualCoupon';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { insurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { useAddCopayCard } from '../queries/useAddCopayCard';
import { CouponOfferDetails } from './CouponOfferDetails';
import { CouponTerms } from './CouponTerms';

type Props = {
  prescription: Prescription;
};

const REVIEW_TERMS_ERROR =
  'Before adding the copay card please review all the terms and conditions and then check the confirmation box to agree.';
const ADD_COPAY_CARD_ERROR = 'Please try again or contact support if the problem persists.';
const MISSING_COPAY_CARD_PARAMS_ERROR = 'Some information is missing. Please contact support if the problem persists.';

export const CouponAttestationActionSheet = ({ prescription }: Props) => {
  const dispatch = useDispatchShared();
  const queryClient = useQueryClient();
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const { mutateAsync: addCopayCard } = useAddCopayCard();
  const userID = useSelectorShared(getCurrentUserID);
  const delivery = useSelectorShared((state) =>
    getNextDeliveryForPrescriptionID(state, {
      prescriptionID: prescription.id,
    }),
  );
  const coupon = useSelectorShared((state) => getAvailableManualCoupon(state, { prescriptionID: prescription.id }));

  const handleHowItWorksPress = () => {
    // TODO: either hardcode on FE or fetch coupon offer details from endpoint
    setActiveActionSheet(
      <CouponOfferDetails
        offerDetails=""
        offerDescription=""
      />,
    );
  };

  const handleAddCoupon = async () => {
    if (!hasAcceptedTerms) {
      setError(REVIEW_TERMS_ERROR);
      return;
    }

    const { ndc: prescriptionNdc, id: prescriptionID } = prescription;
    const deliveryID = delivery?.id;
    const couponID = coupon?.id;

    if (!userID || !prescriptionNdc || !deliveryID || !couponID) {
      setError(MISSING_COPAY_CARD_PARAMS_ERROR);
      return;
    }

    const params = {
      user_id: userID,
      coupon_id: couponID,
      prescription_id: prescriptionID,
      prescription_ndc: prescriptionNdc,
      terms_agreed_to: hasAcceptedTerms,
    };
    const { insurance } = await addCopayCard({ params });

    if (!insurance) {
      setError(ADD_COPAY_CARD_ERROR);
      return;
    }

    // eslint-disable-next-line import/no-deprecated
    const appliedCoupon = await dispatch(applyManualCoupon(deliveryID, insurance.id, prescriptionID));
    if (!appliedCoupon) {
      setError(ADD_COPAY_CARD_ERROR);
    } else {
      queryClient.invalidateQueries({ queryKey: insurances.fetchAll._def });
      closeActionSheet();
      addToast(<Toast>Copay card added. You can view your copay card in your Alto profile.</Toast>);
    }
  };

  return (
    <ActionSheetV2
      title={`Get ${prescription.medication_name || 'your medication'} for as little as $0* by adding a copay card`}
      analyticsName="coupon_attestation"
      buttons={[
        <Button
          key="add-coupon-button"
          label="Add copay card"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={handleAddCoupon}
        />,
        <Button
          key="cancel-coupon-button"
          label="Cancel"
          onPress={closeActionSheet}
          type="tertiary"
        />,
      ]}
      footnote={
        <>
          {error ? (
            <>
              <InlineAlert type="error">
                <Description fontFamily="bold">Could not add copay card</Description>
                <XsSpacing />
                <Description>
                  Before adding the copay card please review all the terms and conditions and then check the
                  confirmation box to agree.
                </Description>
              </InlineAlert>
              <MdSpacing />
            </>
          ) : undefined}
          <Description
            fontFamily="semibold"
            color={COLORS.TEXT_COLORS.PRIMARY}
          >
            Please review all the terms and conditions before confirming you meet the requirements below.
          </Description>
          <MdSpacing />
          <InputCheckbox
            label="By checking this box, I confirm that I meet the defined requirements above."
            name="coupon_attestation"
            onPress={() => {
              setHasAcceptedTerms(!hasAcceptedTerms);
              setError('');
            }}
            value={hasAcceptedTerms}
            noHighlight
            checkPlacement="left"
            fullBleed
          />
          <MdSpacing />
        </>
      }
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Link onPress={handleHowItWorksPress}>How it works</Link>
        <MdSpacing />
        <Body fontFamily="bold">
          By using this copay card, you acknowledge that you currently meet the eligibility criteria and will comply
          with the terms and conditions described below:
        </Body>
        <MdSpacing />
        <CouponTerms />
      </LgPadding>
    </ActionSheetV2>
  );
};
